<template>
    <AppLayout>
        <div class="max-w-7xl mx-auto px-4 py-6">
            <!-- Page Title -->
            <div class="mb-6">
                <h1 class="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                    {{ $tc('LBL_ORDER', 2) }}
                </h1>
                <!-- Breadcrumb -->
                <p class="text-sm text-gray-500 dark:text-gray-300">
                <nav class="text-sm text-gray-500 mb-6" aria-label="Breadcrumb">
                    <ol class="flex items-center space-x-2">
                        <li class="flex items-center">
                            <inertia-link :href="route('landing')" class="text-green-600 hover:underline">
                                {{ $t('LBL_HOME') }}
                            </inertia-link>
                            <svg class="w-3 h-3 mx-2 text-green-400" fill="currentColor" viewBox="0 0 320 512">
                                <path
                                    d="M285.476 272.971L91.132 467.314c-9.373 9.373-24.569 9.373-33.941 0l-22.667-22.667c-9.357-9.357-9.375-24.522-.04-33.901L188.505 256 34.484 101.255c-9.335-9.379-9.317-24.544.04-33.901l22.667-22.667c9.373-9.373 24.569-9.373 33.941 0L285.475 239.03c9.373 9.372 9.373 24.568.001 33.941z" />
                            </svg>
                        </li>
                        <li class="text-gray-700">
                            {{ $tc('LBL_ORDER', 2) }}
                        </li>
                    </ol>
                </nav>
                </p>
            </div>
            <!-- breadcrumb end -->

            <!-- Filter Header -->
            <div class="flex flex-col items-end justify-end gap-4 md:flex-row md:items-end">
                <div class="w-full md:w-1/2">
                    <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div>
                            <label class="block mb-1 text-sm font-medium text-gray-700">Payment Status</label>
                            <t-select v-model="filterBy" placeholder="All" :options="[
                                { text: 'Paid', value: 'Paid' },
                                { text: 'Pending', value: 'Pending' },
                                { text: 'Partial', value: 'Partial' },
                            ]" />
                        </div>
                        <div>
                            <label class="block mb-1 text-sm font-medium text-gray-700">Date Range</label>
                            <t-datepicker v-model="dateRange" placeholder="Date Range" initial-view="month"
                                :range="true" class="w-full" />
                        </div>
                    </div>
                </div>
            </div>

            <!-- Bill Cards -->
            <div v-if="allDetails.bills && allDetails.bills.data.length" class="space-y-6">
                <div v-for="bill in allDetails.bills.data" :key="bill.id"
                    class="bg-white shadow-md rounded-lg overflow-hidden">
                    <!-- Card Header -->
                    <div class="flex items-center justify-between p-4 bg-green-50 cursor-pointer hover:bg-green-100 transition"
                        @click="toggleBill(bill.id)">
                        <div>
                            <p class="font-semibold">
                                Order <span class="text-green-600">#{{ bill.bill_number }} </span>
                            </p>
                            <div v-if="bill.wholesaler || bill.user" class="flex">
                                <span class="mr-1 text-gray-400">
                                    {{ $page.props.roles[0] === 'retailer' ? 'From' : 'By' }}</span>
                                <p class="font-semibold">
                                    {{ $page.props.roles[0] === 'retailer' ? bill.wholesaler.name : bill.user.name }}
                                </p>
                            </div>
                            <p class="text-xs text-gray-500">Placed on {{ bill.created_at | readableDate }}</p>
                        </div>
                        <!-- Product Status Tracker  -->
                        <ProductTracker v-if="isRetailer" :status="bill.delivery_status" />
                        <svg class="w-4 h-4 text-gray-600 transform transition-transform duration-300"
                            :class="{ 'rotate-180': isOpen(bill.id) }" fill="none" viewBox="0 0 24 24"
                            stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                        </svg>
                    </div>

                    <!-- Card Content -->
                    <transition name="fade">
                        <div v-show="isOpen(bill.id)" class="px-4 py-4 space-y-4 text-sm">
                            <!-- Customer Info -->
                            <div class="flex flex-col md:flex-row justify-end gap-4">

                                <div class="flex items-center gap-4">
                                    <t-button variant="success" size="md" @click.stop="togglePaymentListModal(bill)">
                                        Payment List
                                    </t-button>
                                    <t-button v-if="isRetailer && isProductsDelivered(bill) && !isBillPaid(bill)"
                                        variant="success" size="md" @click.stop="togglePaymentDeclarationModal(bill)">
                                        Declare Payment
                                    </t-button>
                                    <button v-if="!isRetailer && (isBillPaid(bill) || isProductsPacked(bill))"
                                        @click.stop="printInvoice(bill)" title="Print Invoice"
                                        class="inline-flex items-center p-2 text-sm text-gray-500 rounded hover:bg-gray-200">
                                        <PrintIcon class="w-4 h-4" />
                                    </button>
                                </div>
                            </div>

                            <!-- Order Details Wrapper -->
                            <div class="flex flex-col md:flex-row gap-6">
                                <!-- Product List -->
                                <div class="flex-1">

                                    <div v-for="order in bill.transactions" :key="order.id"
                                        class="flex flex-col md:flex-row gap-4 p-4 mb-4 bg-white border rounded shadow-sm">
                                        <img :src="order.product.images.length ? order.product.images[0].image_path : common.defaultImg"
                                            class="w-full md:w-32 h-32 object-cover rounded cursor-pointer"
                                            @click="gotoProduct(order)" />
                                        <div class="flex-grow">
                                            <h3 class="text-base font-semibold text-gray-800 cursor-pointer line-clamp-2"
                                                @click="gotoProduct(order)">
                                                {{ order.product.name }}
                                            </h3>
                                            <p class="text-sm text-gray-500 mb-2">{{ order.product.brand.name }}</p>
                                            <div class="text-sm text-gray-600">
                                                <p>Qty: {{ order.qty }}</p>
                                                <p>Price: {{ config(common.currency, order.product.owner) }} {{
                                                    parseFloat(order.product.price).toFixed(2) }}</p>
                                                <p v-if="order.vat != 0">VAT: {{ config(common.currency,
                                                    order.product.owner) }} {{
                                                        order.vat }}</p>
                                            </div>
                                            <div class="mt-2 text-red-500 font-semibold" v-if="order.discount">
                                                <span>Discount: {{ config(common.currency) }} {{ order.discount
                                                }}</span>
                                            </div>
                                            <div class="mt-2 text-green-600 font-semibold">
                                                Total: {{ config(common.currency, order.product.owner) }} {{
                                                    parseFloat(order.grand_total).toFixed(2) }}
                                            </div>
                                        </div>
                                    </div>
                                    <!-- <div v-for="order in bill.transactions" :key="order.id"
                                        class="flex flex-col md:flex-row gap-4 bg-gray-50 p-3 rounded">
                                        <div class="md:w-1/4">
                                            <img :src="order.product.images.length ? order.product.images[0].image_path : common.defaultImg"
                                                class="object-cover h-24 w-full rounded" alt="Product" />
                                        </div>
                                        <div class="flex flex-col justify-between md:w-3/4">
                                            <div class="flex justify-between items-start">
                                                <div>
                                                    <p class="font-semibold">{{ order.product.name }}</p>
                                                    <p class="text-xs text-gray-500">{{ order.product.brand.name }}</p>
                                                </div>
                                                <div class="text-right">
                                                    <p class="text-sm font-medium">Qty: {{ order.qty }}</p>
                                                    <p class="text-sm text-gray-500">
                                                        Rate: {{ config(common.currency) }} {{
                                                            parseFloat(order.product.price).toFixed(2) }}
                                                    </p>
                                                </div>
                                            </div>
                                            <div class="mt-2 border-t pt-2 text-sm space-y-1">
                                                <div class="flex justify-between">
                                                    <span>Subtotal</span>
                                                    <span>{{ config(common.currency) }} {{ (order.qty *
                                                        order.product.price).toFixed(2) }}</span>
                                                </div>
                                                <div class="flex justify-between" v-if="order.vat">
                                                    <span>VAT</span>
                                                    <span>+ {{ config(common.currency) }} {{ order.vat }}</span>
                                                </div>
                                                <div class="flex justify-between text-red-500" v-if="order.discount">
                                                    <span>Discount</span>
                                                    <span>- {{ config(common.currency) }} {{ order.discount }}</span>
                                                </div>
                                                <div class="flex justify-between font-semibold text-green-600">
                                                    <span>Total</span>
                                                    <span>{{ config(common.currency) }} {{ order.grand_total }}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div> -->
                                </div>

                                <!-- Sticky Summary Section -->
                                <div class="md:w-1/3 h-fit sticky top-4">
                                    <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                                        <h2 class="text-lg font-semibold mb-4 text-gray-800">Order Summary</h2>
                                        <div class="text-sm space-y-2">
                                            <div class="flex justify-between">
                                                <span class="text-gray-600">Payment Method</span>
                                                <span class="font-semibold">{{ bill.payment_method }}</span>
                                            </div>
                                            <div class="flex justify-between">
                                                <span class="text-gray-600">Subtotal</span>
                                                <span class="font-semibold">
                                                    {{ config(common.currency) }} {{ subtotalWithVat(bill) }}
                                                </span>
                                            </div>
                                            <div class="flex justify-between">
                                                <span class="text-gray-600">Total Discount</span>
                                                <span class="text-red-500 font-semibold">- {{ config(common.currency) }}
                                                    {{ totalDiscount(bill) }}</span>
                                            </div>
                                            <hr />
                                            <div class="flex justify-between text-lg font-semibold text-green-600">
                                                <span>Grand Total</span>
                                                <span>
                                                    {{ config(common.currency) }}
                                                    {{ parseFloat(bill.total_amount).toFixed(2) }}
                                                </span>
                                            </div>
                                        </div>

                                        <!-- Notes -->
                                        <div class="mt-6">
                                            <h3 class="font-semibold text-sm text-gray-700 mb-1">Notes</h3>
                                            <p class="text-gray-600 text-sm">{{ bill.notes || '--' }}</p>
                                        </div>
                                    </div>
                                </div>

                            </div>
                        </div>
                    </transition>
                </div>

                <div
                        class="flex justify-center items-center bg-gray-100 dark:bg-gray-800 px-4 py-4 border-t dark:border-gray-700"
                    >
                        <t-pagination
                            v-model="currentPage"
                            variant="rounded"
                            :total-items="totalRows"
                            :per-page="perPage"
                            :limit="limit"
                            :disabled="disabled"
                            @change="fetchPageData"
                        />
                    </div>

                <!-- Pagination -->
                <!-- <div class="my-4">
                    <t-pagination v-model="currentPage" :total-items="totalRows" :per-page="perPage" :limit="limit"
                        :disabled="disabled" @change="fetchPageData" />
                </div> -->
            </div>

            <div v-if="allDetails.userBills">
                <div v-if="allDetails.userBills.data.length">
                    <div v-for="(user, index) in allDetails.userBills.data" :key="`bills-${index}`">
                        <div v-for="bill in user.bills" :key="bill.id">
                            <div class="w-full my-3 text-xs bg-white rounded shadow-sm md:text-sm">
                                <div class="flex flex-col md:items-center md:flex-row md:justify-between">
                                    <div class="flex flex-col p-2">
                                        <p class="font-semibold">
                                            Order <span class="text-green-600">#{{ bill.bill_number }} </span>
                                        </p>

                                        <div v-if="bill.wholesaler || bill.user" class="flex">
                                            <span class="mr-1 text-gray-400">
                                                {{ $page.props.roles[0] === 'retailer' || $page.props.roles[0] ===
                                                    'salesperson' ? 'From' : 'By' }}</span>
                                            <p class="font-semibold">
                                                {{ $page.props.roles[0] === 'retailer' || $page.props.roles[0] ===
                                                    'salesperson' ? bill.wholesaler.name : bill.user.name }}
                                            </p>
                                        </div>

                                        <p class="text-xs text-gray-400">
                                            Placed on {{ bill.created_at | readableDate }}
                                        </p>
                                    </div>

                                    <!-- Product Status Tracker  -->
                                    <ProductTracker v-if="isRetailer" :status="bill.delivery_status" />

                                    <div class="flex items-center gap-4 p-2">
                                        <div v-if="!isRetailer">
                                            <t-select :key="deliveryStatusKey + bill.id"
                                                :disabled="isBillPaid(bill) || isBillPartial(bill)"
                                                :value="bill.delivery_status" :options="deliveryStatusList"
                                                @change="updateDeliveryStatus(bill, $event)" />
                                        </div>

                                        <div v-if="isRetailer">
                                            <t-button v-if="isProductsDelivered(bill) && !isBillPaid(bill)"
                                                type="button" @click="togglePaymentDeclarationModal(bill)">
                                                Declare Payment
                                            </t-button>
                                        </div>

                                        <div class="flex items-center gap-4">
                                            <t-button type="button" @click="togglePaymentListModal(bill)">
                                                Payment List
                                            </t-button>

                                            <div v-if="isBillPaid(bill)" class="flex items-center gap-2">
                                                <div class="px-2 py-1 text-white bg-green-400 rounded">
                                                    {{ hasPendingUserPayments(bill) ? 'Declared' : 'Paid' }}
                                                </div>
                                                <button class="p-1 rounded-full cursor-pointer hover:bg-gray-200"
                                                    title="Print Invoice" @click="printInvoice(bill)">
                                                    <PrintIcon class="w-5" />
                                                </button>
                                            </div>

                                            <div v-if="isBillPending(bill)" class="flex items-center gap-2">
                                                <div class="px-4 py-2 bg-blue-300 rounded opacity-50">
                                                    {{ hasPendingUserPayments(bill) ? `Declared
                                                    (${hasPendingUserPayments(bill)})` : 'Pending' }}
                                                </div>
                                                <button
                                                    v-if="(isProductsPacked(bill) || isProductsShipped(bill) || isProductsDelivered(bill))"
                                                    class="p-1 rounded-full cursor-pointer hover:bg-gray-200"
                                                    title="Print Invoice" @click="printInvoice(bill)">
                                                    <ViewIcon v-if="isRetailer" class="w-5 mr-2 fill-current" />
                                                    <PrintIcon v-else class="w-5" />
                                                </button>
                                            </div>

                                            <div v-if="isBillPartial(bill)"
                                                class="px-4 py-2 bg-yellow-300 rounded opacity-50">
                                                Partial
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div v-for="order in bill.transactions" :key="order.id"
                                    class="flex flex-col gap-2 p-5 md:flex-row md:gap-4">
                                    <div class="flex flex-col mb-3 md:flex-row md:gap-3 md:w-2/5">
                                        <div class="flex-shrink-0 w-full md:w-1/3">
                                            <img :src="order.product.images.length > 0 ? order.product.images[0].image_path : common.defaultImg"
                                                alt="Product Image" class="w-full h-32 cursor-pointer md:h-28"
                                                @click="goto('products.show', { dummy: `${convertToKebabCase(order.product.brand.name)}-${convertToKebabCase(order.product.name)}`, product: order.product.slug })">
                                        </div>
                                        <div class="mt-1 md:flex-grow">
                                            <div class="mb-4 font-semibold cursor-pointer line-clamp-3"
                                                @click="goto('products.show', { dummy: `${convertToKebabCase(order.product.brand.name)}-${convertToKebabCase(order.product.name)}`, product: order.product.slug })">
                                                {{ order.product.name }}
                                            </div>
                                            <div class="flex text-gray-500">
                                                <img v-if="order.product.brand.images.length"
                                                    :src="order.product.brand.images[0].image_path" class="h-5 mr-2">
                                                {{ order.product.brand.name }}
                                            </div>
                                        </div>
                                    </div>
                                    <div class="md:w-1/5">
                                        <div>Qty: {{ order.qty }}</div>
                                        <div>Price/Piece: {{ config(common.currency, order.product.owner) }} {{
                                            parseFloat(order.product.price).toFixed(2) }}</div>
                                    </div>
                                    <div class="flex-col justify-center p-1 rounded md:w-2/5 md:flex bg-gray-50">
                                        <div class="flex justify-between">
                                            <p>Subtotal ({{ order.qty }} item)</p>
                                            <p>{{ config(common.currency, order.product.owner) }} {{ (order.qty *
                                                order.product.price).toFixed(2) }}</p>
                                        </div>
                                        <div v-if="order.vat != 0" class="flex justify-between">
                                            <p>Vat</p>
                                            <p>+ {{ config(common.currency, order.product.owner) }} {{ order.vat }}</p>
                                        </div>
                                        <!-- <div class="flex justify-between">
                                            <p>Discount</p>
                                            <p class="text-red-500">
                                                - {{ config(common.currency, order.product.owner) }} {{ parseFloat(order.discount).toFixed(2) }}
                                            </p>
                                        </div> -->
                                        <hr>
                                        <div class="flex justify-between mt-1 text-lg">
                                            <p>Total</p>
                                            <p class="font-semibold text-green-500">
                                                {{ config(common.currency, order.product.owner) }} {{
                                                    parseFloat(order.grand_total).toFixed(2) }}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div class="flex items-center justify-between p-5">
                                    <div class="flex flex-grow pr-6 mt-1 text-md md:w-2/5">
                                        <p class="mr-2">
                                            Notes:
                                        </p>
                                        <p class="font-semibold">
                                            {{ bill.notes ? bill.notes : '--' }}
                                        </p>
                                    </div>
                                    <div class="pl-4 md:w-2/5">
                                        <div class="flex justify-between mt-1 font-semibold">
                                            <p>Payment Method</p>
                                            <p>
                                                {{ (bill.payment_method) }}
                                            </p>
                                        </div>
                                        <div class="flex justify-between">
                                            <p>Discount</p>
                                            <p class="text-red-500">
                                                - {{ config(common.currency) }} {{ totalDiscount(bill) }}
                                            </p>
                                        </div>
                                        <div class="flex justify-between mt-1 text-lg ">
                                            <p>Grand total</p>
                                            <p class="font-semibold text-green-500">
                                                {{ config(common.currency) }} {{
                                                    parseFloat(bill.total_amount).toFixed(2) }}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Pagination -->
                    <div class="my-4">
                        <t-pagination v-model="currentPage" :total-items="totalRows" :per-page="perPage" :limit="limit"
                            :disabled="disabled" @change="fetchPageData" />
                    </div>
                </div>
            </div>

            <div v-if="!(allDetails.bills && allDetails.bills.data.length) && !(allDetails.userBills)" class="my-10 w-100" style="text-align: center">
                No order available.
            </div>
        </div>

        <t-modal v-if="currentBill && isRetailer" v-model="paymentDeclarationModal" :click-to-close="false"
            header="Enter the amount you paid the wholesaler.">
            <div class="flex flex-col">
                <div class="flex mt-1 rounded-md shadow-sm">
                    <span
                        class="inline-flex items-center px-3 text-sm text-gray-500 border border-r-0 border-gray-300 rounded-l-md bg-gray-50">
                        {{ config(common.currency) }}
                    </span>
                    <t-input v-model="amountPaid" class="rounded-tl-none rounded-bl-none" type="number" />
                </div>

                <img v-if="amountReceiptPreview" :src="amountReceiptPreview"
                    class="object-cover object-center w-full h-full mt-4 rounded" @click="replaceReceipt">

                <label v-else for="file-upload-input"
                    class="relative font-medium text-green-600 bg-white rounded-md cursor-pointer hover:text-green-500">
                    <div ref="fileUpload"
                        class="flex justify-center px-6 pt-5 pb-6 mt-2 border-2 border-gray-300 border-dashed rounded-md">
                        <div class="flex flex-col items-center justify-center space-y-1 text-center">
                            <FileUploadIcon />
                            <div class="flex items-center justify-center text-sm text-gray-600">
                                <span>Upload a receipt</span>
                                <input id="file-upload-input" ref="file-upload-input" name="file-upload-input"
                                    type="file" class="sr-only" @change="filesChange($event.target.files)">
                            </div>
                            <p class="text-xs text-gray-500">
                                PNG, JPG, GIF up to 5MB
                            </p>
                        </div>
                    </div>
                </label>
            </div>
            <template #footer>
                <div class="flex justify-end gap-4">
                    <t-button :disabled="!!!amountPaid" variant="success" type="button" @click="paymentDeclaration">
                        Ok
                    </t-button>
                    <t-button type="button" @click="togglePaymentDeclarationModal()">
                        Cancel
                    </t-button>
                </div>
            </template>
        </t-modal>

        <t-modal v-if="currentBill" v-model="paymentListModal" header="List of payments">
            <div v-if="currentBill.user_payments.length">
                <t-table :headers="['Amount', 'Receipt', 'Date', 'Action']" :data="currentBill.user_payments"
                    class="text-xs md:text-base">
                    <template slot="row" slot-scope="{ trClass, tdClass, row }">
                        <tr :class="[trClass, 'text-xs md:text-base']">
                            <td :class="[tdClass]">
                                {{ config(common.currency) }} {{ parseFloat(row.amount).toFixed(2) }}
                            </td>
                            <td :class="[tdClass]">
                                <div class="text-blue-500 cursor-pointer hover:text-blue-700"
                                    @click="openReceiptInNewTab(row.receipt)">
                                    Open Receipt
                                </div>
                            </td>
                            <td :class="[tdClass]">
                                {{ row.created_at | fromNow }}
                            </td>
                            <td :class="[tdClass]">
                                <div v-if="!row.accepted_at && !row.deleted_at && isWholesaler" class="flex gap-2">
                                    <div class="px-2 py-1 text-xs text-white bg-green-400 rounded cursor-pointer md:text-sm"
                                        @click="paymentConfirm(row.bill_id, row.id)">
                                        Confirm
                                    </div>
                                    <div class="px-2 py-1 text-xs text-white bg-red-400 rounded cursor-pointer md:text-sm"
                                        @click="paymentReject(row.bill_id, row.id)">
                                        Reject
                                    </div>
                                </div>
                                <div v-else-if="!row.accepted_at && !row.deleted_at && !isWholesaler">
                                    Pending
                                </div>
                                <div v-else-if="row.accepted_at && !row.deleted_at && !isWholesaler"
                                    class="inline-block px-2 py-1 text-sm text-white bg-green-400 rounded">
                                    Accepted
                                </div>
                                <div v-else-if="!row.accepted_at && row.deleted_at"
                                    class="inline-block px-2 py-1 text-sm text-white bg-red-400 rounded">
                                    Rejected
                                </div>
                            </td>
                        </tr>
                    </template>
                </t-table>
            </div>
            <div v-else>
                No payment declaration made yet.
            </div>
        </t-modal>
    </AppLayout>
</template>

<script>
import AppLayout from '../../Layouts/AppLayout.vue';
import ProductTracker from '../../Components/ProductTracker.vue';
import common from '../../common';
import PrintIcon from '../../Components/Icons/PrintIcon.vue';
import ViewIcon from '../../Components/Icons/ViewIcon.vue';
import FileUploadIcon from '../../Components/Icons/FileUploadIcon.vue';

export default {
    name: 'PurchaseHistoryIndex',

    components: {
        AppLayout,
        ProductTracker,
        PrintIcon,
        ViewIcon,
        FileUploadIcon,
    },

    props: {
        allDetails: {
            type: Object,
            default: null,
        },
    },

    data() {
        return {
            common,
            dateRange: [],
            mounted: false,
            paymentDeclarationModal: false,
            paymentListModal: false,
            amountPaid: 0,
            amountReceipt: null,
            amountReceiptPreview: null,
            currentBill: null,
            deliveryStatusList: ['Placed', 'Packed', 'Shipped', 'Delivered'],
            currentPage: null,
            limit: 5,
            perPage: this.allDetails.bills ? this.allDetails.bills.per_page : this.allDetails.userBills.per_page,
            totalRows: this.allDetails.bills ? this.allDetails.bills.total : this.allDetails.userBills.total,
            filterBy: null,
            dialogIsOpen: false,
            deliveryStatusKey: 0,
            collapsedBills: {},
        };
    },

    computed: {
        disabled() {
            console.log(this.allDetails.bills);
            return this.allDetails.bills ? !this.allDetails.bills.data.length : !this.allDetails.userBills.data.length;
        },
    },

    watch: {

        'allDetails.bills.data': {
            handler(newVal) {
                // Expand all bills by default
                if (newVal && Array.isArray(newVal)) {
                    newVal.forEach(bill => {
                        this.$set(this.collapsedBills, bill.id, true);
                    });
                }
            },
            immediate: true,
        },

        dateRange: {
            handler(newVal) {
                if ((newVal.length === 2 || newVal.length === 0) && this.mounted) {
                    this.$inertia.get(route('account.index', { from: this.dateRange[0], to: this.dateRange[1] }));
                }
            },
        },

        filterBy() {
            if (this.mounted) {
                this.fetchPageData();
            }
        },
    },

    created() {
        this.currentPage = this.allDetails.bills ? this.allDetails.bills.current_page : this.allDetails.userBills.current_page;
    },

    mounted() {
        if (this.allDetails.filtersApplied.from && this.allDetails.filtersApplied.to) {
            this.dateRange = [this.allDetails.filtersApplied.from, this.allDetails.filtersApplied.to];
        }
        if (this.allDetails.filtersApplied.status) this.filterBy = this.allDetails.filtersApplied.status;
        // wait for a sec so that the watch for dateRange doesn't get executed on mounted hook
        this.sleep(1000).then(() => {
            this.mounted = true;
        });
    },

    methods: {
        printInvoice(bill) {
            this.newTab('invoice.print', { billId: bill.id });
        },

        toggleBill(id) {
            this.$set(this.collapsedBills, id, !this.collapsedBills[id]);
        },
        isOpen(id) {
            return this.collapsedBills[id];
        },

        subtotalWithVat(bill) {
            return bill.transactions
                .reduce((previousValue, currentValue) => {
                    const totalWithVat = parseFloat(currentValue.total) + parseFloat(currentValue.vat);
                    return previousValue + totalWithVat;
                }, 0)
                .toFixed(2);
        },

        totalDiscount(bill) {
            return bill.transactions.map((order) => order.discount).reduce((previousValue, currentValue) => parseInt(previousValue) + parseInt(currentValue), 0).toFixed(2);
        },

        updateDeliveryStatus(bill, value) {
            if (this.deliveryStatusList.includes(value) && !this.isBillPaid(bill) && this.dialogIsOpen === false) {
                const payload = {
                    delivery_status: value,
                    _method: 'PUT',
                };

                this.dialogIsOpen = true;

                this.$dialog
                    .confirm({
                        title: 'Confirm',
                        text: `Are you sure you want to update the delivery status to ${value}?`,
                    })
                    .then((result) => {
                        if (result.isOk) {
                            this.$inertia.post(route('paymentStatus.update', bill.id), payload);
                        } else {
                            this.deliveryStatusKey += 1;
                        }
                        this.dialogIsOpen = false;
                    });
            }
        },

        isBillPaid(bill) {
            if (bill.payment_status === 'Paid') {
                return true;
            }
            return false;
        },

        isProductsDelivered(bill) {
            if (bill.delivery_status === 'Delivered') {
                return true;
            }
            return false;
        },

        isProductsPacked(bill) {
            if (bill.delivery_status === 'Packed') {
                return true;
            }
            return false;
        },

        isProductsShipped(bill) {
            if (bill.delivery_status === 'Shipped') {
                return true;
            }
            return false;
        },

        isBillPending(bill) {
            if (bill.payment_status === 'Pending') {
                return true;
            }
            return false;
        },

        isBillPartial(bill) {
            if (bill.payment_status === 'Partial') {
                return true;
            }
            return false;
        },

        paymentDeclaration() {
            if (this.amountPaid) {
                const formData = new FormData();
                // needed to send this request as put request else
                // this will be read as post in the server even if we call post method
                formData.append('_method', 'PUT');
                formData.append('amount', this.amountPaid);
                if (this.amountReceipt) {
                    formData.append('receipt', this.amountReceipt);
                }

                this.$inertia.post(route('paymentDeclaration', this.currentBill.id), formData, {
                    onSuccess: () => {
                        this.paymentDeclarationModal = false;
                    },
                });
            }
        },

        paymentConfirm(billId, paymentId) {
            this.$inertia.get(route('paymentConfirm', { bill: billId, payment: paymentId }));
        },

        paymentReject(billId, paymentId) {
            this.$inertia.get(route('paymentReject', { bill: billId, payment: paymentId }));
        },

        togglePaymentDeclarationModal(bill = null) {
            if (bill !== null) {
                this.currentBill = bill;
                this.paymentDeclarationModal = true;
                this.amountPaid = parseFloat(bill.amount_remaining).toFixed(2);
            } else {
                this.currentBill = null;
                this.paymentDeclarationModal = false;
                this.amountPaid = 0;
                this.amountReceipt = null;
            }
        },

        togglePaymentListModal(bill = null) {
            if (bill !== null) {
                this.currentBill = bill;
                this.paymentListModal = true;
            } else {
                this.currentBill = null;
                this.paymentListModal = false;
            }
        },

        fetchPageData(newPage) {
            this.$inertia.get(
                route('account.index', {
                    from: this.dateRange[0],
                    to: this.dateRange[1],
                    page: newPage,
                    filterBy: this.filterBy,
                })
            );
        },

        filesChange(fileList) {
            if (!fileList.length) return;
            this.amountReceipt = fileList[0];
            this.getImagePreviews();
        },

        getImagePreviews() {
            if (/\.(jpe?g|png|gif)$/i.test(this.amountReceipt.name)) {
                const reader = new FileReader();
                reader.addEventListener('load', () => {
                    this.amountReceiptPreview = (reader.result);
                }, false);

                reader.readAsDataURL(this.amountReceipt);
            }
        },

        replaceReceipt() {
            this.$refs['file-upload-input'].click();
        },

        openReceiptInNewTab(receipt) {
            window.open(`/storage/${receipt}`);
        },

        hasPendingUserPayments(bill) {
            const pendingBills = bill.user_payments.filter(
                (payment) => payment.accepted_at === null && payment.deleted_at === null
            );
            return pendingBills.length;
        },
    },
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: all 0.3s ease;
}

.fade-enter,
.fade-leave-to {
    opacity: 0;
    transform: translateY(-5px);
}
</style>
